import React from 'react';
import { NotificationType, UserNotification } from '../../../../model/notifications/notifications';
import VendingTokenDetail from './detail/vending-token-notification/VendingTokenDetail';
import styles from './NotificationDetail.module.scss';
import ServiceRequestPaymentDetail from './detail/service-request-payment-notification/ServiceRequestPaymentDetail';
import WalletDebitedDetail from './detail/wallet-debited-notification/WalletDebitedDetail';
import WalletCreditedDetail from './detail/wallet-credited-notification/WalletCreditedDetail';
import BpsBillingDetail from './detail/bps-billing-notification/BpsBillingDetail';
import SolarRecommendationDetail from './detail/solar-recommendation-notification/SolarRecommendationDetail';
import StatementOfAccountDetail from './detail/statement-account-notification/StatementAccountDetails';
import BroadcastNotificationDetails from './detail/broadcast-notification-details/BroadcastNotificationDetails';

interface NotificationDetailProps {
    notification: UserNotification | null;
    isLoading?: boolean;
    onClose: () => void;
    hideCloseButton?: boolean;
}

const formatKey = (key: string): string =>
    key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const ContextValue: React.FC<{ value: unknown; depth?: number }> = ({ value, depth = 0 }) => {
    if (value === null || value === undefined) {
        return <span className={styles.contextNull}>—</span>;
    }
    if (Array.isArray(value)) {
        if (value.length === 0) return <span className={styles.contextNull}>[]</span>;
        return (
            <ul className={styles.contextList}>
                {value.map((item, i) => (
                    <li key={i}>
                        <ContextValue value={item} depth={depth + 1} />
                    </li>
                ))}
            </ul>
        );
    }
    if (typeof value === 'object') {
        return <ContextFields data={value as Record<string, unknown>} depth={depth + 1} />;
    }
    return <span className={styles.contextScalar}>{String(value)}</span>;
};

const ContextFields: React.FC<{ data: Record<string, unknown>; depth?: number }> = ({ data, depth = 0 }) => (
    <dl className={`${styles.contextFields} ${depth > 0 ? styles.contextFieldsNested : ''}`}>
        {Object.entries(data).map(([key, val]) => (
            <div key={key} className={styles.contextRow}>
                <dt className={styles.contextKey}>{formatKey(key)}</dt>
                <dd className={styles.contextValue}>
                    <ContextValue value={val} depth={depth} />
                </dd>
            </div>
        ))}
    </dl>
);

const parseContext = (raw: string): Record<string, unknown> | string => {
    if (raw && typeof (raw as any) === 'object') return raw as unknown as Record<string, unknown>;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
        return raw;
    } catch {
        return raw;
    }
};

const renderContext = (notification: UserNotification) => {
    const context = parseContext(notification.notificationContext);

    switch (notification.type) {
        case NotificationType.VendingToken:
            return <VendingTokenDetail context={context as any} />;
        case NotificationType.ServiceRequestPayment:
            return <ServiceRequestPaymentDetail context={context as any} />;
        case NotificationType.WalletDebited:
            return <WalletDebitedDetail context={context as any} />;
        case NotificationType.WalletCredited:
            return <WalletCreditedDetail context={context as any} />;
        case NotificationType.BpsBilling:
            return <BpsBillingDetail context={context as any} notificationId={notification.id} title={notification.title} message={notification.message} />;
        case NotificationType.SolarRecommendation:
            return <SolarRecommendationDetail context={context as any} />;
        case NotificationType.StatementOfAccountReport:
                return <StatementOfAccountDetail context={context as any} />;
        case NotificationType.Broadcast:
            return <BroadcastNotificationDetails context={context as any} />;
        default:
            return typeof context === 'object' ? (
                <ContextFields data={context} />
            ) : (
                <p className={styles.message}>{notification.notificationContext}</p>
            );
    }
};

const NotificationDetail: React.FC<NotificationDetailProps> = ({ notification, isLoading, onClose, hideCloseButton }) => {
    return (
        <div className={styles.panel}>
            {!hideCloseButton && (
                <div className={styles.header}>
                    <div className={styles.headerTitle}>
                        <button className={styles.closeBtn} onClick={onClose} aria-label="Close detail">
                            ←
                        </button>
                        {notification && (
                            <div className={styles.titleRow}>
                                <h2 className={styles.title}>{notification.title}</h2>
                            </div>
                        )}
                    </div>
                    {notification && (
                        <div className={styles.meta}>
                            <span className={styles.metaItem}>{notification.message}</span>
                        </div>
                    )}
                </div>
            )}

            {isLoading ? (
                <div className={styles.loading}>
                    <p>Loading...</p>
                </div>
            ) : notification ? (
                <>
                    <div className={styles.body}>
                        {renderContext(notification)}
                    </div>
                  
                </>
            ) : null}
        </div>
    );
};

export default NotificationDetail;
